import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import type { Tables } from "@/integrations/db/types.generated";
import { useInAppEnabled } from "@/hooks/useNotificationPreferences";

/**
 * An assignment notification with the two relations the list view embeds.
 *
 * Columns are taken from the generated row types rather than restated; only
 * the *selection* is written out here, because that is a property of the query
 * rather than of the schema. The previous hand-written version had first_name
 * and last_name as non-nullable, which the database has never guaranteed.
 */
export type AssignmentNotification = Tables<"assignment_notifications"> & {
  backlog_item?: Pick<Tables<"backlog_items">, "id" | "task" | "row_number" | "ticket_number" | "deleted_at"> | null;
  assigned_by?: Pick<Tables<"profiles">, "id" | "first_name" | "last_name" | "email"> | null;
  /** Set when the notification is "this landed on <Team>" rather than "you were assigned". */
  team?: Pick<Tables<"teams">, "id" | "name" | "color"> | null;
};

// Fetch assignment notifications. Hidden, not deleted, when someone turns
// in-app assignment notifications off (#9112): the rows stay, because the
// email outbox reads them.
export const useAssignmentNotifications = () => {
  const inApp = useInAppEnabled("assigned");
  return useQuery({
    queryKey: ["assignment-notifications"],
    select: (notifications) => (inApp ? notifications : []),
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("assignment_notifications")
        .select(
          `
          *,
          backlog_item:backlog_items(
            id,
            task,
            row_number,
            ticket_number,
            deleted_at
          ),
          assigned_by:profiles!assigned_by_id(
            id,
            first_name,
            last_name,
            email
          ),
          team:teams(
            id,
            name,
            color
          )
        `,
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Filter out notifications for deleted backlog items
      // Deliberately not annotated: the row type is inferred from the select,
      // including the embedded backlog_item, so a column rename breaks here.
      return (data ?? []).filter((n) => n.backlog_item?.deleted_at === null);
    },
  });
};

// Count unread assignment notifications
export const useUnreadAssignmentCount = () => {
  const inApp = useInAppEnabled("assigned");
  return useQuery({
    queryKey: ["unread-assignment-count"],
    select: (count) => (inApp ? count : 0),
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return 0;

      // The inner join + deleted_at filter mirror the list query's predicate
      // (`n.backlog_item?.deleted_at === null`): a notification whose ticket is
      // soft-deleted or purged is invisible there, so it must not be counted
      // here — otherwise the badge shows ghosts with nothing to click.
      const { count, error } = await supabase
        .from("assignment_notifications")
        .select("id, backlog_item:backlog_items!inner(id)", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false)
        .is("backlog_item.deleted_at", null);

      if (error) throw error;
      return count || 0;
    },
  });
};

// Mark assignment notification as read
export const useMarkAssignmentAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("assignment_notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);

      if (error) throw error;
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: ["assignment-notifications"] });
      await queryClient.cancelQueries({ queryKey: ["unread-assignment-count"] });

      const previousNotifications = queryClient.getQueryData(["assignment-notifications"]);
      const previousCount = queryClient.getQueryData(["unread-assignment-count"]);

      queryClient.setQueryData<AssignmentNotification[]>(["assignment-notifications"], (old) => {
        if (!old) return old;
        return old.map((n) =>
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n,
        );
      });

      queryClient.setQueryData<number>(["unread-assignment-count"], (old) => {
        return Math.max(0, (old || 0) - 1);
      });

      return { previousNotifications, previousCount };
    },
    onError: (_err, _notificationId, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(["assignment-notifications"], context.previousNotifications);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(["unread-assignment-count"], context.previousCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["assignment-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unread-assignment-count"] });
    },
  });
};

// Mark all assignment notifications as read
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const now = new Date().toISOString();

      // Mark all assignment notifications as read
      const { error: assignmentError } = await supabase
        .from("assignment_notifications")
        .update({ is_read: true, read_at: now })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (assignmentError) throw assignmentError;
    },
    onMutate: async () => {
      // Cancel outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: ["assignment-notifications"] });
      await queryClient.cancelQueries({ queryKey: ["unread-assignment-count"] });

      // Snapshot previous values for rollback
      const previousNotifications = queryClient.getQueryData(["assignment-notifications"]);
      const previousCount = queryClient.getQueryData(["unread-assignment-count"]);

      // Optimistically mark all as read
      queryClient.setQueryData<AssignmentNotification[]>(["assignment-notifications"], (old) => {
        if (!old) return old;
        return old.map((n) => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString(),
        }));
      });

      // Optimistically set count to 0
      queryClient.setQueryData<number>(["unread-assignment-count"], 0);

      return { previousNotifications, previousCount };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(["assignment-notifications"], context.previousNotifications);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(["unread-assignment-count"], context.previousCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["assignment-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unread-assignment-count"] });
    },
  });
};
